import React from 'react';
/**
 * Lightweight markdown renderer for legal analysis output.
 * Supports: headings (# through ######), bold (**text**), italic (*text*),
 * inline code (`code`), code blocks (```...```), bullet lists (- * +),
 * numbered lists (1.), nested/indented lists, blockquotes (>),
 * horizontal rules (---), and paragraphs.
 */
interface MarkdownViewProps {
  content: string;
  className?: string;
}
function renderInline(text: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  // Match: ***bold-italic***, **bold**, *italic*, `code`
  const regex = /(\*\*\*[^*]+\*\*\*|\*\*[^*]+\*\*|\*[^*\n]+\*|`[^`]+`)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    const token = match[0];
    if (token.startsWith('***') && token.endsWith('***')) {
      nodes.push(<strong key={`bi-${key++}`} className="font-semibold italic text-slate-900" data-id="element-150">
          
          {token.slice(3, -3)}
        </strong>);
    } else if (token.startsWith('**') && token.endsWith('**')) {
      nodes.push(<strong key={`b-${key++}`} className="font-semibold text-slate-900" data-id="element-151">
          {token.slice(2, -2)}
        </strong>);
    } else if (token.startsWith('`') && token.endsWith('`')) {
      nodes.push(<code key={`c-${key++}`} className="px-1.5 py-0.5 bg-slate-100 text-slate-800 rounded text-[0.9em] font-mono" data-id="element-152">
          
          {token.slice(1, -1)}
        </code>);
    } else if (token.startsWith('*') && token.endsWith('*')) {
      nodes.push(<em key={`i-${key++}`} className="italic" data-id="element-153">
          {token.slice(1, -1)}
        </em>);
    }
    lastIndex = match.index + token.length;
  }
  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }
  return nodes;
}
const HEADING_CLASSES: Record<number, string> = {
  1: 'text-xl font-bold text-slate-900 mt-5 mb-2',
  2: 'text-lg font-bold text-slate-900 mt-5 mb-2',
  3: 'text-base font-semibold text-slate-900 mt-4 mb-2',
  4: 'text-sm font-semibold text-slate-900 mt-3 mb-1.5',
  5: 'text-sm font-semibold text-slate-700 mt-3 mb-1',
  6: 'text-xs font-semibold text-slate-600 mt-2 mb-1 uppercase tracking-wide'
};
const BULLET_RE = /^([-*+])\s+(.*)$/;
const ORDERED_RE = /^(\d+)\.\s+(.*)$/;
interface ListItem {
  content: string;
  indent: number;
}
function renderListItems(items: ListItem[], ordered: boolean, keyPrefix: string): React.ReactNode {
  // Group items by indent level — simplest approach: render as flat list with nested padding
  // Or actually nest them properly using a recursive grouping
  const baseIndent = Math.min(...items.map(i => i.indent));
  const root: {
    content: string;
    children: ListItem[];
  }[] = [];
  let currentParent: {
    content: string;
    children: ListItem[];
  } | null = null;
  for (const item of items) {
    if (item.indent === baseIndent) {
      currentParent = {
        content: item.content,
        children: []
      };
      root.push(currentParent);
    } else if (currentParent) {
      currentParent.children.push(item);
    }
  }
  const ListTag = ordered ? 'ol' : 'ul';
  const listClass = ordered ? 'list-decimal pl-6 my-2 space-y-1 text-slate-700' : 'list-disc pl-6 my-2 space-y-1 text-slate-700';
  return <ListTag key={keyPrefix} className={listClass} data-id="element-154">
      {root.map((node, idx) => <li key={`${keyPrefix}-${idx}`} className="leading-relaxed" data-id="element-155">
          {renderInline(node.content)}
          {node.children.length > 0 && renderListItems(node.children, ordered, `${keyPrefix}-${idx}-sub`)}
        </li>)}
    </ListTag>;
}
export function MarkdownView({
  content,
  className = ''
}: MarkdownViewProps) {
  if (!content) return null;
  const lines = content.split('\n');
  const blocks: React.ReactNode[] = [];
  let i = 0;
  let key = 0;
  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();
    // Empty line — skip
    if (!trimmed) {
      i++;
      continue;
    }
    // Code fence
    if (/^```/.test(trimmed)) {
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !/^```/.test(lines[i].trim())) {
        codeLines.push(lines[i]);
        i++;
      }
      i++; // skip closing fence
      blocks.push(<pre key={`code-${key++}`} className="my-3 p-3 bg-slate-900 text-slate-100 rounded-lg overflow-x-auto text-xs font-mono" data-id="element-156">
          
          <code data-id="element-157">{codeLines.join('\n')}</code>
        </pre>);
      continue;
    }
    // Horizontal rule
    if (/^-{3,}$|^\*{3,}$|^_{3,}$/.test(trimmed)) {
      blocks.push(<hr key={`hr-${key++}`} className="my-4 border-slate-200" data-id="element-158" />);
      i++;
      continue;
    }
    // Headings (must come before list/bullet check)
    const headingMatch = trimmed.match(/^(#{1,6})\s+(.*)$/);
    if (headingMatch) {
      const level = headingMatch[1].length;
      const text = headingMatch[2];
      const Tag = `h${level}` as keyof JSX.IntrinsicElements;
      blocks.push(<Tag key={`h-${key++}`} className={HEADING_CLASSES[level]} data-id="element-159">
          {renderInline(text)}
        </Tag>);
      i++;
      continue;
    }
    // Blockquote
    if (trimmed.startsWith('>')) {
      const quoteLines: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith('>')) {
        quoteLines.push(lines[i].trim().replace(/^>\s?/, ''));
        i++;
      }
      blocks.push(<blockquote key={`q-${key++}`} className="border-l-4 border-slate-300 pl-4 py-1 my-3 text-slate-600 italic" data-id="element-160">
          
          {renderInline(quoteLines.join(' '))}
        </blockquote>);
      continue;
    }
    // Unordered list (allow leading whitespace for nested items)
    if (BULLET_RE.test(trimmed)) {
      const items: ListItem[] = [];
      while (i < lines.length) {
        const ln = lines[i];
        const ltrim = ln.trim();
        if (!BULLET_RE.test(ltrim)) {
          // Continuation of previous bullet? Stop on blank or different block type.
          if (!ltrim) break;
          if (/^#{1,6}\s/.test(ltrim) || ORDERED_RE.test(ltrim) || ltrim.startsWith('>') || /^```/.test(ltrim)) {
            break;
          }
          // Treat as continuation of last item
          if (items.length > 0) {
            items[items.length - 1].content += ' ' + ltrim;
            i++;
            continue;
          }
          break;
        }
        const indent = ln.length - ln.trimStart().length;
        const m = ltrim.match(BULLET_RE)!;
        items.push({
          content: m[2],
          indent
        });
        i++;
      }
      blocks.push(renderListItems(items, false, `ul-${key++}`));
      continue;
    }
    // Ordered list
    if (ORDERED_RE.test(trimmed)) {
      const items: ListItem[] = [];
      while (i < lines.length) {
        const ln = lines[i];
        const ltrim = ln.trim();
        if (!ORDERED_RE.test(ltrim)) {
          if (!ltrim) break;
          if (/^#{1,6}\s/.test(ltrim) || BULLET_RE.test(ltrim) || ltrim.startsWith('>') || /^```/.test(ltrim)) {
            break;
          }
          if (items.length > 0) {
            items[items.length - 1].content += ' ' + ltrim;
            i++;
            continue;
          }
          break;
        }
        const indent = ln.length - ln.trimStart().length;
        const m = ltrim.match(ORDERED_RE)!;
        items.push({
          content: m[2],
          indent
        });
        i++;
      }
      blocks.push(renderListItems(items, true, `ol-${key++}`));
      continue;
    }
    // Paragraph — collect consecutive non-empty, non-special lines
    const paraLines: string[] = [trimmed];
    i++;
    while (i < lines.length) {
      const next = lines[i];
      const nextTrimmed = next.trim();
      if (!nextTrimmed || /^#{1,6}\s/.test(nextTrimmed) || BULLET_RE.test(nextTrimmed) || ORDERED_RE.test(nextTrimmed) || nextTrimmed.startsWith('>') || /^```/.test(nextTrimmed) || /^-{3,}$|^\*{3,}$|^_{3,}$/.test(nextTrimmed)) {
        break;
      }
      paraLines.push(nextTrimmed);
      i++;
    }
    blocks.push(<p key={`p-${key++}`} className="my-2 leading-relaxed text-slate-700" data-id="element-161">
        {renderInline(paraLines.join(' '))}
      </p>);
  }
  return <div className={className} data-id="element-162">{blocks}</div>;
}